import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { ajvResolver } from '@hookform/resolvers/ajv';
import { XCircle } from '@phosphor-icons/react';

import type { ActionInputFormBlock, ActionInputs, ActionInputsSchema } from '../types';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { SimpleSelect } from '@/components/select';
import { cn } from '@/lib/utils';
import { DatePicker } from '@/components/date-picker';

export interface ActionInputFormBlockProps {
  block: ActionInputFormBlock;
}

/**
 * Patches the schema for AJV resolver to correctly validate required string fields.
 *
 * This function addresses a limitation in AJV validation:
 * By default, AJV only checks if a field exists but doesn't consider empty strings invalid.
 * For form validation, we need to ensure required string fields are non-empty.
 * This function adds `minLength: 1` to all required string fields that don't already
 * have a minLength constraint, ensuring empty strings fail validation.
 * see https://github.com/react-hook-form/resolvers/blob/master/README.md#ajv
 *
 * @param schema - The original JSON schema to enhance
 * @returns A new schema with minLength constraints added to required string fields
 */
export function patchSchemaForAjvResolver(schema: ActionInputsSchema): ActionInputsSchema {
  // Use structuredClone for proper deep copying of all data types
  const newSchema = structuredClone(schema);

  newSchema.errorMessage = 'This field is required';

  for (const [key, property] of Object.entries((newSchema.properties ?? {}) as Record<string, any>)) {
    if (newSchema.required?.includes(key) && property.type === 'string') {
      property.minLength = property.minLength || 1;
      property.errorMessage = property.errorMessage || {
        minLength: 'This field is required',
      };
    }
    if (property.type === 'number') {
      property.errorMessage = property.errorMessage || {
        type: 'Please enter a valid number',
      };
    }
  }

  return newSchema;
}

export function ActionInputFormBlock({ block }: ActionInputFormBlockProps) {
  const formSchema = React.useMemo(() => {
    return patchSchemaForAjvResolver(block.schema);
  }, [block.schema]);

  const { inputs = [], initialValues, state } = block;

  const isReady = state === 'ready';
  const isProcessing = state === 'processing';
  const isCompleted = state === 'completed';

  const defaultValues = Object.entries(initialValues).reduce((acc, [key, value]) => {
    if (typeof value === 'string' && value.trim() === '') {
      return acc;
    }
    acc[key] = value;

    return acc;
  }, {} as ActionInputs);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ActionInputs>({
    defaultValues,
    resolver: ajvResolver(formSchema, {
      removeAdditional: true,
    }),
  });

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (block.onSubmit) {
      void handleSubmit(block.onSubmit)(e);
    }

    return false;
  };

  const formInputs = inputs.map((input) => {
    const { name, required, schema: inputSchema, ui } = input;
    const labelText = `${inputSchema.title ?? name}${required ? '*' : ''}`;
    const description = inputSchema.description;
    const error = errors[name];

    const errorMessage = error?.message;

    let descriptionText: React.ReactNode = description ? String(description) : null;

    if (error) {
      // if there is a description, show it and the error message in italic
      descriptionText = description ? (
        <>
          {description} - <span className="italic">{errorMessage}</span>
        </>
      ) : (
        // if there is no description, just show the error message
        errorMessage
      );
    }

    const inputType = ui?.type || 'text';
    const registerOptions = {
      valueAsNumber: inputType === 'number' ? true : undefined,
    };

    return (
      <div key={name} className="py-2 flex flex-col gap-2">
        <Label htmlFor={name}>{labelText}</Label>
        {inputType === 'datepicker' ? (
          <Controller
            control={control}
            name={name}
            render={({ field }) => (
              <DatePicker
                date={field.value && typeof field.value === 'string' ? new Date(field.value) : undefined}
                setDate={(date) => {
                  if (date) {
                    field.onChange(date.toISOString());
                  } else {
                    field.onChange(undefined);
                  }
                }}
                className={cn('!bg-transparent', { 'border-red-600': !!error })}
              />
            )}
          />
        ) : inputType === 'textarea' ? (
          <Textarea
            disabled={!isReady}
            rows={2}
            maxLength={10000}
            className={cn('!bg-transparent', { 'border-red-600': !!error })}
            {...register(name, registerOptions)}
            aria-invalid={!!error}
            aria-required={required}
          />
        ) : inputType === 'select' ? (
          <Controller
            control={control}
            name={name}
            render={({ field }) => (
              <SimpleSelect
                options={
                  inputSchema.oneOf?.map((option) => ({
                    label: option.title,
                    value: option.const,
                  })) ?? []
                }
                className={cn('!bg-transparent', { 'border-red-600': !!error })}
                value={field.value as string}
                onChange={(value) => field.onChange(value)}
              />
            )}
          />
        ) : (
          <Input
            type={inputType}
            disabled={!isReady}
            className={cn({ 'border-red-600': !!error })}
            {...register(name, registerOptions)}
            aria-invalid={!!error}
            aria-required={required}
          />
        )}
        {descriptionText && (
          <span
            className={cn('flex text-sm leading-6 font-medium gap-1.5 items-center', {
              'text-red-600 dark:text-white': !!error,
            })}
          >
            {error && <XCircle weight="fill" size={20} className="inline-block align-middle text-red-600" />}
            <span>{descriptionText}</span>
          </span>
        )}
      </div>
    );
  });

  return (
    <div className="relative">
      <form className="flex flex-col justify-stretch" onSubmit={onSubmit}>
        {formInputs}
        <div className="mt-4">
          <Button type="submit" variant="dark" disabled={!isReady} className="w-full rounded-s">
            {isReady && 'Submit'}
            {isProcessing && 'Processing...'}
            {isCompleted && 'Completed'}
          </Button>
        </div>
      </form>
    </div>
  );
}
